import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import PreviewIcon from '@mui/icons-material/Preview';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SettingsIcon from '@mui/icons-material/Settings';
import axios from 'axios';
import EditorPlantillaPdf from './EditorPlantillaPdf';

const FORMATOS = ['A4', 'A5', 'T80'];

const initialForm = {
  nombre: '',
  tipo_comprobante: '01',
  formato: 'A4',
  config_json: {
    showLogo: true,
    showObservaciones: true,
    showQr: true,
    showHash: true,
    showSunatMessage: true
  }
};

const initialDuplicarForm = {
  nombre: '',
  tipo_comprobante: '03',
  asignar_ahora: true
};

const initialNuevaAsignacionForm = {
  tipo_comprobante: '01',
  formato: 'A4',
  sucursal_id: '',
  plantilla_id: ''
};

export default function PlantillasPdf() {
  const [plantillas, setPlantillas] = useState([]);
  const [asignaciones, setAsignaciones] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [tiposDocumento, setTiposDocumento] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [mensaje, setMensaje] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [asignacionDraft, setAsignacionDraft] = useState({});
  const [openEditAsignacion, setOpenEditAsignacion] = useState(false);
  const [asignacionEditar, setAsignacionEditar] = useState(null);
  const [openEditorAvanzado, setOpenEditorAvanzado] = useState(false);
  const [plantillaEditorAvanzado, setPlantillaEditorAvanzado] = useState(null);
  const [openDuplicar, setOpenDuplicar] = useState(false);
  const [plantillaDuplicarOrigen, setPlantillaDuplicarOrigen] = useState(null);
  const [duplicarForm, setDuplicarForm] = useState(initialDuplicarForm);
  const [previewComprobanteId, setPreviewComprobanteId] = useState('1');
  const [openRenombrar, setOpenRenombrar] = useState(false);
  const [plantillaRenombrar, setPlantillaRenombrar] = useState(null);
  const [renombrarNombre, setRenombrarNombre] = useState('');
  const [openConfirmarEliminar, setOpenConfirmarEliminar] = useState(false);
  const [plantillaEliminar, setPlantillaEliminar] = useState(null);
  const [openNuevaAsignacion, setOpenNuevaAsignacion] = useState(false);
  const [nuevaAsignacionForm, setNuevaAsignacionForm] = useState(initialNuevaAsignacionForm);
  const [openConfirmarEliminarAsignacion, setOpenConfirmarEliminarAsignacion] = useState(false);
  const [asignacionEliminar, setAsignacionEliminar] = useState(null);
  const importInputRef = useRef(null);
  // Gestión de tipos de documento
  const [openGestionTipos, setOpenGestionTipos] = useState(false);
  const [openNuevoTipo, setOpenNuevoTipo] = useState(false);
  const [openEditarTipo, setOpenEditarTipo] = useState(false);
  const [openConfirmarEliminarTipo, setOpenConfirmarEliminarTipo] = useState(false);
  const [tipoEditando, setTipoEditando] = useState(null);
  const [tipoEliminar, setTipoEliminar] = useState(null);
  const [tipoForm, setTipoForm] = useState({ codigo: '', nombre: '', descripcion: '', orden: 99 });

  const parseConfigPlantilla = (value) => {
    try {
      if (!value) return {};
      if (typeof value === 'string') return JSON.parse(value);
      if (typeof value === 'object') return value;
      return {};
    } catch (e) {
      return {};
    }
  };

  const fetchTipos = async () => {
    try {
      const res = await axios.get('/api/reporte-pdf/tipos-documento');
      setTiposDocumento(res.data || []);
    } catch (_) {
      // Si la tabla aún no existe, usamos fallback estático
      setTiposDocumento([
        { id: 1, codigo: '01', nombre: 'Factura', es_electronico: true },
        { id: 2, codigo: '03', nombre: 'Boleta', es_electronico: true },
        { id: 3, codigo: '07', nombre: 'Nota de credito', es_electronico: true },
        { id: 4, codigo: '08', nombre: 'Nota de debito', es_electronico: true },
        { id: 5, codigo: '09', nombre: 'Guia remision remitente', es_electronico: true },
        { id: 6, codigo: '31', nombre: 'Guia remision transportista', es_electronico: true }
      ]);
    }
  };

  const fetchData = async () => {
    try {
      const [plantillasRes, asignacionesRes, sucursalesRes] = await Promise.allSettled([
        axios.get('/api/reporte-pdf/plantillas'),
        axios.get('/api/reporte-pdf/asignaciones'),
        axios.get('/api/sucursales')
      ]);

      setPlantillas(plantillasRes.status === 'fulfilled' ? (plantillasRes.value.data || []) : []);
      setAsignaciones(asignacionesRes.status === 'fulfilled' ? (asignacionesRes.value.data || []) : []);
      setSucursales(sucursalesRes.status === 'fulfilled' ? (sucursalesRes.value.data || []) : []);
    } catch (err) {
      setMensaje({ tipo: 'error', texto: `No se pudo cargar la configuracion PDF. ${err.response?.data?.error || err.message}` });
    }
  };

  useEffect(() => {
    fetchData();
    fetchTipos();
  }, []);

  const plantillasPorTipoFormato = useMemo(() => {
    const map = {};
    for (const p of plantillas) {
      const key = `${p.tipo_comprobante || 'GLOBAL'}-${p.formato}`;
      if (!map[key]) map[key] = [];
      map[key].push(p);
    }
    return map;
  }, [plantillas]);

  const opcionesPlantilla = (tipo, formato) => {
    const especificas = plantillasPorTipoFormato[`${tipo}-${formato}`] || [];
    const globales = plantillasPorTipoFormato[`GLOBAL-${formato}`] || [];
    return [...especificas, ...globales];
  };

  const abrirNueva = () => {
    setForm(initialForm);
    setOpenNew(true);
  };

  const handleImportarPlantilla = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      if (!data.nombre || !data.config_json) {
        setMensaje({ tipo: 'error', texto: 'Archivo JSON inválido. Debe contener nombre y config_json.' });
        return;
      }
      await axios.post('/api/reporte-pdf/plantillas', {
        nombre: data.nombre,
        formato: data.formato || 'A4',
        tipo_comprobante: data.tipo_comprobante || '01',
        config_json: typeof data.config_json === 'string' ? data.config_json : JSON.stringify(data.config_json),
        usuario: 'admin'
      });
      setMensaje({ tipo: 'success', texto: `Plantilla "${data.nombre}" importada correctamente.` });
      await fetchData();
    } catch (err) {
      setMensaje({ tipo: 'error', texto: `Error al importar plantilla: ${err.response?.data?.error || err.message}` });
    } finally {
      if (importInputRef.current) importInputRef.current.value = '';
    }
  };

  const crearPlantilla = async () => {
    try {
      setGuardando(true);
      await axios.post('/api/reporte-pdf/plantillas', { ...form, usuario: 'admin' });
      setMensaje({ tipo: 'success', texto: 'Plantilla creada correctamente.' });
      setOpenNew(false);
      await fetchData();
    } catch (err) {
      setMensaje({ tipo: 'error', texto: `No se pudo crear la plantilla. ${err.response?.data?.error || err.message}` });
    } finally {
      setGuardando(false);
    }
  };

  const asignar = async (tipo, formato) => {
    const key = `${tipo}-${formato}`;
    const plantillaId = Number(asignacionDraft[key] || 0);
    if (!plantillaId) {
      setMensaje({ tipo: 'warning', texto: `Selecciona una plantilla para ${tipo} ${formato}.` });
      return;
    }

    try {
      await axios.post('/api/reporte-pdf/asignaciones', {
        plantilla_id: plantillaId,
        tipo_comprobante: tipo,
        formato,
        sucursal_id: null,
        usuario: 'admin'
      });
      setMensaje({ tipo: 'success', texto: `Plantilla asignada a ${tipo} ${formato}.` });
      await fetchData();
    } catch (err) {
      setMensaje({ tipo: 'error', texto: `No se pudo asignar la plantilla. ${err.response?.data?.error || err.message}` });
    }
  };

  const abrirEditor = (plantilla) => {
    setPlantillaEditorAvanzado(plantilla);
    setOpenEditorAvanzado(true);
  };

  const abrirDuplicar = (plantilla) => {
    const sugerenciaPorTipo = {
      '01': '03',
      '03': '01',
      '07': '08',
      '08': '07',
      '09': '31',
      '31': '09'
    };
    const tipoSugerido = sugerenciaPorTipo[String(plantilla?.tipo_comprobante || '')] || '03';
    const tipoNombre = tiposDocumento.find((t) => t.codigo === tipoSugerido)?.nombre || tipoSugerido;
    setPlantillaDuplicarOrigen(plantilla);
    setDuplicarForm({
      nombre: `${plantilla?.nombre || 'Plantilla'} - copia ${tipoNombre}`,
      tipo_comprobante: tipoSugerido,
      asignar_ahora: true
    });
    setOpenDuplicar(true);
  };

  const duplicarPlantilla = async () => {
    if (!plantillaDuplicarOrigen) return;
    if (!duplicarForm.nombre.trim()) {
      setMensaje({ tipo: 'warning', texto: 'Ingresa un nombre para la plantilla duplicada.' });
      return;
    }

    try {
      setGuardando(true);

      const configOriginal = parseConfigPlantilla(plantillaDuplicarOrigen.config_json);
      const createResp = await axios.post('/api/reporte-pdf/plantillas', {
        nombre: duplicarForm.nombre.trim(),
        tipo_comprobante: duplicarForm.tipo_comprobante,
        formato: plantillaDuplicarOrigen.formato,
        config_json: configOriginal,
        usuario: 'admin'
      });

      const nuevaPlantillaId = createResp?.data?.id;
      if (duplicarForm.asignar_ahora && nuevaPlantillaId) {
        await axios.post('/api/reporte-pdf/asignaciones', {
          plantilla_id: Number(nuevaPlantillaId),
          tipo_comprobante: duplicarForm.tipo_comprobante,
          formato: plantillaDuplicarOrigen.formato,
          sucursal_id: null,
          usuario: 'admin'
        });
      }

      setOpenDuplicar(false);
      setPlantillaDuplicarOrigen(null);
      setDuplicarForm(initialDuplicarForm);
      setMensaje({ tipo: 'success', texto: duplicarForm.asignar_ahora ? 'Plantilla duplicada y asignada correctamente.' : 'Plantilla duplicada correctamente.' });
      await fetchData();
    } catch (err) {
      setMensaje({ tipo: 'error', texto: `No se pudo duplicar la plantilla. ${err.response?.data?.error || err.message}` });
    } finally {
      setGuardando(false);
    }
  };

  const abrirRenombrar = (plantilla) => {
    setPlantillaRenombrar(plantilla);
    setRenombrarNombre(plantilla.nombre);
    setOpenRenombrar(true);
  };

  const guardarRenombrar = async () => {
    if (!plantillaRenombrar) return;
    if (!renombrarNombre.trim()) {
      setMensaje({ tipo: 'warning', texto: 'El nombre no puede estar vacio.' });
      return;
    }
    try {
      setGuardando(true);
      const configOriginal = parseConfigPlantilla(plantillaRenombrar.config_json);
      await axios.put(`/api/reporte-pdf/plantillas/${plantillaRenombrar.id}`, {
        nombre: renombrarNombre.trim(),
        config_json: configOriginal,
        usuario: 'admin'
      });
      setMensaje({ tipo: 'success', texto: 'Nombre actualizado correctamente.' });
      setOpenRenombrar(false);
      setPlantillaRenombrar(null);
      await fetchData();
    } catch (err) {
      setMensaje({ tipo: 'error', texto: `No se pudo renombrar. ${err.response?.data?.error || err.message}` });
    } finally {
      setGuardando(false);
    }
  };

  const confirmarEliminar = (plantilla) => {
    setPlantillaEliminar(plantilla);
    setOpenConfirmarEliminar(true);
  };

  const ejecutarEliminar = async () => {
    if (!plantillaEliminar) return;
    try {
      setGuardando(true);
      await axios.delete(`/api/reporte-pdf/plantillas/${plantillaEliminar.id}`);
      setMensaje({ tipo: 'success', texto: `Plantilla "${plantillaEliminar.nombre}" eliminada.` });
      setOpenConfirmarEliminar(false);
      setPlantillaEliminar(null);
      await fetchData();
    } catch (err) {
      setMensaje({ tipo: 'error', texto: `No se pudo eliminar. ${err.response?.data?.error || err.message}` });
    } finally {
      setGuardando(false);
    }
  };

  const abrirEditarAsignacion = (item) => {
    setAsignacionEditar({ ...item, nuevaPlantilla: String(item.plantilla_id) });
    setOpenEditAsignacion(true);
  };

  const abrirNuevaAsignacion = () => {
    setNuevaAsignacionForm(initialNuevaAsignacionForm);
    setOpenNuevaAsignacion(true);
  };

  const crearNuevaAsignacion = async () => {
    const { tipo_comprobante, formato, sucursal_id, plantilla_id } = nuevaAsignacionForm;
    if (!plantilla_id) {
      setMensaje({ tipo: 'warning', texto: 'Selecciona una plantilla.' });
      return;
    }
    try {
      setGuardando(true);
      await axios.post('/api/reporte-pdf/asignaciones', {
        plantilla_id: Number(plantilla_id),
        tipo_comprobante,
        formato,
        sucursal_id: sucursal_id ? Number(sucursal_id) : null,
        usuario: 'admin'
      });
      const ambito = sucursal_id ? `Sucursal ${sucursal_id}` : 'Global';
      setMensaje({ tipo: 'success', texto: `Asignacion creada: ${tipo_comprobante} ${formato} (${ambito}).` });
      setOpenNuevaAsignacion(false);
      await fetchData();
    } catch (err) {
      setMensaje({ tipo: 'error', texto: `No se pudo crear la asignacion. ${err.response?.data?.error || err.message}` });
    } finally {
      setGuardando(false);
    }
  };

  const confirmarEliminarAsignacion = (item) => {
    setAsignacionEliminar(item);
    setOpenConfirmarEliminarAsignacion(true);
  };

  const ejecutarEliminarAsignacion = async () => {
    if (!asignacionEliminar) return;
    try {
      setGuardando(true);
      await axios.delete(`/api/reporte-pdf/asignaciones/${asignacionEliminar.id}`);
      setMensaje({ tipo: 'success', texto: 'Asignacion eliminada correctamente.' });
      setOpenConfirmarEliminarAsignacion(false);
      setAsignacionEliminar(null);
      await fetchData();
    } catch (err) {
      setMensaje({ tipo: 'error', texto: `No se pudo eliminar la asignacion. ${err.response?.data?.error || err.message}` });
    } finally {
      setGuardando(false);
    }
  };

  const guardarEditarAsignacion = async () => {
    if (!asignacionEditar?.nuevaPlantilla) return;
    try {
      setGuardando(true);
      await axios.put(`/api/reporte-pdf/asignaciones/${asignacionEditar.id}`, {
        plantilla_id: Number(asignacionEditar.nuevaPlantilla),
        usuario: 'admin'
      });
      setMensaje({ tipo: 'success', texto: 'Asignacion actualizada correctamente.' });
      setOpenEditAsignacion(false);
      await fetchData();
    } catch (err) {
      setMensaje({ tipo: 'error', texto: `No se pudo actualizar la asignacion. ${err.response?.data?.error || err.message}` });
    } finally {
      setGuardando(false);
    }
  };

  const preview = (formato, tipoCodigo) => {
    const id = String(previewComprobanteId || '').trim();
    if (!id) return;
    // Para tipos internos (COT, NV) usar sus propios endpoints
    const tipoUpper = String(tipoCodigo || '').toUpperCase();
    let url;
    if (tipoUpper === 'COT') {
      url = `/api/reporte-pdf/cotizacion/${id}?formato=${formato}&mode=inline`;
    } else if (tipoUpper === 'NV') {
      url = `/api/reporte-pdf/nota-venta/${id}?formato=${formato}&mode=inline`;
    } else {
      url = `/api/reporte-pdf/comprobante/${id}?formato=${formato}&mode=inline`;
    }
    window.open(url, '_blank');
  };

  // ─── Handlers: Gestión de tipos de documento ─────────────────────────────

  const abrirNuevoTipo = () => {
    setTipoForm({ codigo: '', nombre: '', descripcion: '', orden: 99 });
    setOpenNuevoTipo(true);
  };

  const crearTipo = async () => {
    if (!tipoForm.codigo.trim() || !tipoForm.nombre.trim()) {
      setMensaje({ tipo: 'warning', texto: 'El codigo y nombre son obligatorios.' });
      return;
    }
    try {
      setGuardando(true);
      await axios.post('/api/reporte-pdf/tipos-documento', { ...tipoForm, usuario: 'admin' });
      setMensaje({ tipo: 'success', texto: `Tipo "${tipoForm.nombre}" creado.` });
      setOpenNuevoTipo(false);
      await fetchTipos();
    } catch (err) {
      setMensaje({ tipo: 'error', texto: err.response?.data?.error || err.message });
    } finally {
      setGuardando(false);
    }
  };

  const abrirEditarTipo = (tipo) => {
    setTipoEditando(tipo);
    setTipoForm({ codigo: tipo.codigo, nombre: tipo.nombre, descripcion: tipo.descripcion || '', orden: tipo.orden });
    setOpenEditarTipo(true);
  };

  const guardarEditarTipo = async () => {
    if (!tipoEditando || !tipoForm.nombre.trim()) return;
    try {
      setGuardando(true);
      await axios.put(`/api/reporte-pdf/tipos-documento/${tipoEditando.id}`, { ...tipoForm, usuario: 'admin' });
      setMensaje({ tipo: 'success', texto: 'Tipo actualizado.' });
      setOpenEditarTipo(false);
      setTipoEditando(null);
      await fetchTipos();
    } catch (err) {
      setMensaje({ tipo: 'error', texto: err.response?.data?.error || err.message });
    } finally {
      setGuardando(false);
    }
  };

  const confirmarEliminarTipo = (tipo) => {
    setTipoEliminar(tipo);
    setOpenConfirmarEliminarTipo(true);
  };

  const ejecutarEliminarTipo = async () => {
    if (!tipoEliminar) return;
    try {
      setGuardando(true);
      await axios.delete(`/api/reporte-pdf/tipos-documento/${tipoEliminar.id}`);
      setMensaje({ tipo: 'success', texto: `Tipo "${tipoEliminar.nombre}" eliminado.` });
      setOpenConfirmarEliminarTipo(false);
      setTipoEliminar(null);
      await fetchTipos();
    } catch (err) {
      setMensaje({ tipo: 'error', texto: err.response?.data?.error || err.message });
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 1, mb: 4, width: '100%' }}>
      <Box sx={{ width: { xs: '100%', sm: '95%', md: '90%' }, maxWidth: 1800 }}>
        <Typography variant="h4" gutterBottom sx={{ mb: 3, fontWeight: 'bold', color: '#222' }}>
          Plantillas PDF
        </Typography>

        {mensaje && (
          <Alert severity={mensaje.tipo} sx={{ mb: 2 }} onClose={() => setMensaje(null)}>
            {mensaje.texto}
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={8}>
            <Alert severity="info">
              Configura plantillas por tipo y formato. Usa el boton Diseno para editar estilo completo.
            </Alert>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, display: 'flex', gap: 1.5, alignItems: 'center', justifyContent: 'space-between' }}>
              <TextField
                label="ID comprobante preview"
                size="small"
                value={previewComprobanteId}
                onChange={(e) => setPreviewComprobanteId(e.target.value)}
                sx={{ width: 140 }}
              />
              <Button variant="outlined" startIcon={<SettingsIcon />} onClick={() => setOpenGestionTipos(true)} size="small">
                Tipos
              </Button>
              <Button variant="contained" startIcon={<AddIcon />} onClick={abrirNueva} size="small">
                Nueva
              </Button>
              <Button variant="outlined" size="small" onClick={() => importInputRef.current?.click()}>
                Importar JSON
              </Button>
              <input
                type="file"
                accept=".json"
                style={{ display: 'none' }}
                ref={importInputRef}
                onChange={handleImportarPlantilla}
              />
            </Paper>
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          {tiposDocumento.map((tipo) => (
            <Grid item xs={12} key={tipo.codigo}>
              <Card sx={{ borderRadius: 3, boxShadow: 4 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      {tipo.nombre} ({tipo.codigo})
                    </Typography>
                    {!tipo.es_electronico && (
                      <Chip label="Interno" size="small" color="info" />
                    )}
                  </Box>
                  <Grid container spacing={2}>
                    {FORMATOS.map((formato) => {
                      const key = `${tipo.codigo}-${formato}`;
                      const actual = asignaciones.find((a) => a.tipo_comprobante === tipo.codigo && a.formato === formato && !a.sucursal_id);
                      const opciones = opcionesPlantilla(tipo.codigo, formato);

                      return (
                        <Grid item xs={12} md={4} key={key}>
                          <Paper sx={{ p: 2, borderRadius: 3, minHeight: 240, background: 'linear-gradient(180deg, #ffffff 0%, #f7f9fc 100%)' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{formato}</Typography>
                              {actual ? <Chip label="Activo" color="success" size="small" /> : <Chip label="Sin asignar" size="small" />}
                            </Box>

                            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
                              Actual: {actual?.plantilla_nombre || 'Ninguna'}
                            </Typography>

                            <TextField
                              select
                              fullWidth
                              size="small"
                              label="Seleccionar plantilla"
                              value={asignacionDraft[key] || actual?.plantilla_id || ''}
                              onChange={(e) => setAsignacionDraft((prev) => ({ ...prev, [key]: e.target.value }))}
                              sx={{ mb: 1.5 }}
                            >
                              {opciones.length === 0 ? (
                                <MenuItem value="">No hay plantillas</MenuItem>
                              ) : (
                                opciones.map((p) => <MenuItem key={p.id} value={p.id}>{p.nombre}</MenuItem>)
                              )}
                            </TextField>

                            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                              <Button fullWidth variant="contained" startIcon={<AssignmentTurnedInIcon />} onClick={() => asignar(tipo.codigo, formato)}>
                                Asignar
                              </Button>
                              <Button fullWidth variant="outlined" startIcon={<PreviewIcon />} onClick={() => preview(formato, tipo.codigo)}>
                                Preview
                              </Button>
                            </Box>
                          </Paper>
                        </Grid>
                      );
                    })}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <TableContainer component={Paper} sx={{ borderRadius: 4, boxShadow: '0 4px 24px rgba(0,0,0,0.08)', mt: 2, overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell align="center">ID</TableCell>
                <TableCell align="center">Nombre</TableCell>
                <TableCell align="center">Tipo</TableCell>
                <TableCell align="center">Formato</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {plantillas.map((plantilla) => (
                <TableRow key={plantilla.id} hover>
                  <TableCell align="center">{plantilla.id}</TableCell>
                  <TableCell align="center">{plantilla.nombre}</TableCell>
                  <TableCell align="center">{tiposDocumento.find((t) => t.codigo === plantilla.tipo_comprobante)?.nombre || plantilla.tipo_comprobante || 'Global'}</TableCell>
                  <TableCell align="center">{plantilla.formato}</TableCell>
                  <TableCell align="center">
                    <Button size="small" startIcon={<EditIcon />} onClick={() => abrirEditor(plantilla)}>
                      Diseno
                    </Button>
                    <Button size="small" startIcon={<DriveFileRenameOutlineIcon />} onClick={() => abrirRenombrar(plantilla)}>
                      Renombrar
                    </Button>
                    <Button size="small" startIcon={<ContentCopyIcon />} onClick={() => abrirDuplicar(plantilla)}>
                      Duplicar
                    </Button>
                    <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => confirmarEliminar(plantilla)}>
                      Eliminar
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TableContainer component={Paper} sx={{ borderRadius: 4, boxShadow: '0 4px 24px rgba(0,0,0,0.08)', mt: 3, overflowX: 'auto' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, pt: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Asignaciones activas</Typography>
            <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={abrirNuevaAsignacion}>
              Nueva asignacion
            </Button>
          </Box>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell align="center">Tipo</TableCell>
                <TableCell align="center">Formato</TableCell>
                <TableCell align="center">Ambito</TableCell>
                <TableCell align="center">Plantilla</TableCell>
                <TableCell align="center">Fecha</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {asignaciones.length === 0 ? (
                <TableRow>
                  <TableCell align="center" colSpan={6}>Sin asignaciones registradas</TableCell>
                </TableRow>
              ) : (
                asignaciones.map((item) => (
                  <TableRow key={item.id} hover>
                    <TableCell align="center">{tiposDocumento.find((t) => t.codigo === item.tipo_comprobante)?.nombre || item.tipo_comprobante}</TableCell>
                    <TableCell align="center">{item.formato}</TableCell>
                    <TableCell align="center">{item.sucursal_id ? (item.sucursal_nombre || `Sucursal ${item.sucursal_id}`) : 'Global'}</TableCell>
                    <TableCell align="center">{item.plantilla_nombre}</TableCell>
                    <TableCell align="center">{item.fecha_creacion ? new Date(item.fecha_creacion).toLocaleString('es-PE') : '-'}</TableCell>
                    <TableCell align="center">
                      <Button size="small" startIcon={<EditIcon />} onClick={() => abrirEditarAsignacion(item)}>
                        Cambiar
                      </Button>
                      <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => confirmarEliminarAsignacion(item)}>
                        Eliminar
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Dialog open={openNew} onClose={() => setOpenNew(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Nueva plantilla PDF</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 1, display: 'grid', gap: 2 }}>
              <TextField label="Nombre" value={form.nombre} onChange={(e) => setForm((prev) => ({ ...prev, nombre: e.target.value }))} fullWidth />
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField select label="Tipo comprobante" fullWidth value={form.tipo_comprobante} onChange={(e) => setForm((prev) => ({ ...prev, tipo_comprobante: e.target.value }))}>
                    {tiposDocumento.map((tipo) => <MenuItem key={tipo.codigo} value={tipo.codigo}>{tipo.nombre}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField select label="Formato" fullWidth value={form.formato} onChange={(e) => setForm((prev) => ({ ...prev, formato: e.target.value }))}>
                    {FORMATOS.map((f) => <MenuItem key={f} value={f}>{f}</MenuItem>)}
                  </TextField>
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenNew(false)}>Cancelar</Button>
            <Button onClick={crearPlantilla} variant="contained" startIcon={<SaveIcon />} disabled={guardando || !form.nombre.trim()}>
              {guardando ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogActions>
        </Dialog>

        <EditorPlantillaPdf
          open={openEditorAvanzado}
          onClose={() => setOpenEditorAvanzado(false)}
          plantilla={plantillaEditorAvanzado}
          onSave={async () => {
            setMensaje({ tipo: 'success', texto: 'Plantilla actualizada correctamente.' });
            await fetchData();
          }}
          loading={guardando}
        />

        <Dialog open={openEditAsignacion} onClose={() => setOpenEditAsignacion(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Cambiar Plantilla Asignada</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2, display: 'grid', gap: 2 }}>
              {asignacionEditar && (
                <>
                  <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                    <Typography variant="body2"><strong>Tipo:</strong> {tiposDocumento.find((t) => t.codigo === asignacionEditar.tipo_comprobante)?.nombre || asignacionEditar.tipo_comprobante}</Typography>
                    <Typography variant="body2"><strong>Formato:</strong> {asignacionEditar.formato}</Typography>
                    <Typography variant="body2"><strong>Ambito:</strong> {asignacionEditar.sucursal_id ? (asignacionEditar.sucursal_nombre || `Sucursal ${asignacionEditar.sucursal_id}`) : 'Global'}</Typography>
                  </Box>

                  <TextField
                    select
                    fullWidth
                    label="Nueva plantilla"
                    value={asignacionEditar.nuevaPlantilla || ''}
                    onChange={(e) => setAsignacionEditar((prev) => ({ ...prev, nuevaPlantilla: e.target.value }))}
                  >
                    {plantillas
                      .filter((p) => (p.tipo_comprobante === asignacionEditar.tipo_comprobante || !p.tipo_comprobante) && p.formato === asignacionEditar.formato)
                      .map((p) => (
                        <MenuItem key={p.id} value={String(p.id)}>
                          {p.nombre}
                        </MenuItem>
                      ))}
                  </TextField>
                </>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenEditAsignacion(false)}>Cancelar</Button>
            <Button onClick={guardarEditarAsignacion} variant="contained" startIcon={<SaveIcon />} disabled={guardando}>
              {guardando ? 'Guardando...' : 'Cambiar'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={openRenombrar} onClose={() => setOpenRenombrar(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Renombrar Plantilla</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              {plantillaRenombrar && (
                <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                  Tipo: {tiposDocumento.find((t) => t.codigo === plantillaRenombrar.tipo_comprobante)?.nombre || plantillaRenombrar.tipo_comprobante || 'Global'} &bull; Formato: {plantillaRenombrar.formato}
                </Typography>
              )}
              <TextField
                fullWidth
                label="Nuevo nombre"
                value={renombrarNombre}
                onChange={(e) => setRenombrarNombre(e.target.value)}
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && guardarRenombrar()}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenRenombrar(false)}>Cancelar</Button>
            <Button onClick={guardarRenombrar} variant="contained" startIcon={<SaveIcon />} disabled={guardando || !renombrarNombre.trim()}>
              {guardando ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={openConfirmarEliminar} onClose={() => setOpenConfirmarEliminar(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Confirmar Eliminacion</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 1 }}>
              <Typography>
                ¿Eliminar la plantilla <strong>{plantillaEliminar?.nombre}</strong>?
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                Esta accion no se puede deshacer. Si tiene asignaciones activas, la eliminacion sera bloqueada.
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenConfirmarEliminar(false)}>Cancelar</Button>
            <Button onClick={ejecutarEliminar} variant="contained" color="error" startIcon={<DeleteIcon />} disabled={guardando}>
              {guardando ? 'Eliminando...' : 'Eliminar'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={openDuplicar} onClose={() => setOpenDuplicar(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Duplicar Plantilla</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2, display: 'grid', gap: 2 }}>
              {plantillaDuplicarOrigen && (
                <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                  <Typography variant="body2"><strong>Origen:</strong> {plantillaDuplicarOrigen.nombre}</Typography>
                  <Typography variant="body2"><strong>Tipo origen:</strong> {tiposDocumento.find((t) => t.codigo === plantillaDuplicarOrigen.tipo_comprobante)?.nombre || plantillaDuplicarOrigen.tipo_comprobante || 'Global'}</Typography>
                  <Typography variant="body2"><strong>Formato:</strong> {plantillaDuplicarOrigen.formato}</Typography>
                </Box>
              )}

              <TextField
                fullWidth
                label="Nombre nueva plantilla"
                value={duplicarForm.nombre}
                onChange={(e) => setDuplicarForm((prev) => ({ ...prev, nombre: e.target.value }))}
              />

              <TextField
                select
                fullWidth
                label="Tipo destino"
                value={duplicarForm.tipo_comprobante}
                onChange={(e) => setDuplicarForm((prev) => ({ ...prev, tipo_comprobante: e.target.value }))}
              >
                {tiposDocumento.map((tipo) => (
                  <MenuItem key={tipo.codigo} value={tipo.codigo}>{tipo.nombre}</MenuItem>
                ))}
              </TextField>

              <TextField
                select
                fullWidth
                label="Asignar ahora"
                value={duplicarForm.asignar_ahora ? 'si' : 'no'}
                onChange={(e) => setDuplicarForm((prev) => ({ ...prev, asignar_ahora: e.target.value === 'si' }))}
              >
                <MenuItem value="si">Si, asignar automaticamente en global</MenuItem>
                <MenuItem value="no">No, solo crear copia</MenuItem>
              </TextField>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDuplicar(false)}>Cancelar</Button>
            <Button onClick={duplicarPlantilla} variant="contained" startIcon={<ContentCopyIcon />} disabled={guardando || !duplicarForm.nombre.trim()}>
              {guardando ? 'Duplicando...' : 'Duplicar'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog: Nueva Asignacion */}
        <Dialog open={openNuevaAsignacion} onClose={() => setOpenNuevaAsignacion(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Nueva Asignacion de Plantilla</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2, display: 'grid', gap: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    select fullWidth label="Tipo comprobante"
                    value={nuevaAsignacionForm.tipo_comprobante}
                    onChange={(e) => setNuevaAsignacionForm((prev) => ({ ...prev, tipo_comprobante: e.target.value, plantilla_id: '' }))}
                  >
                    {tiposDocumento.map((t) => <MenuItem key={t.codigo} value={t.codigo}>{t.nombre}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    select fullWidth label="Formato"
                    value={nuevaAsignacionForm.formato}
                    onChange={(e) => setNuevaAsignacionForm((prev) => ({ ...prev, formato: e.target.value, plantilla_id: '' }))}
                  >
                    {FORMATOS.map((f) => <MenuItem key={f} value={f}>{f}</MenuItem>)}
                  </TextField>
                </Grid>
              </Grid>

              <TextField
                select fullWidth label="Ambito"
                value={nuevaAsignacionForm.sucursal_id}
                onChange={(e) => setNuevaAsignacionForm((prev) => ({ ...prev, sucursal_id: e.target.value }))}
              >
                <MenuItem value="">Global (todas las sucursales)</MenuItem>
                {sucursales.map((s) => <MenuItem key={s.id} value={String(s.id)}>{s.nombre}</MenuItem>)}
              </TextField>

              <TextField
                select fullWidth label="Plantilla a asignar"
                value={nuevaAsignacionForm.plantilla_id}
                onChange={(e) => setNuevaAsignacionForm((prev) => ({ ...prev, plantilla_id: e.target.value }))}
              >
                {plantillas
                  .filter((p) => p.tipo_comprobante === nuevaAsignacionForm.tipo_comprobante && p.formato === nuevaAsignacionForm.formato)
                  .map((p) => <MenuItem key={p.id} value={String(p.id)}>{p.nombre}</MenuItem>)}
                {plantillas.filter((p) => p.tipo_comprobante === nuevaAsignacionForm.tipo_comprobante && p.formato === nuevaAsignacionForm.formato).length === 0 && (
                  <MenuItem value="" disabled>No hay plantillas para este tipo/formato</MenuItem>
                )}
              </TextField>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenNuevaAsignacion(false)}>Cancelar</Button>
            <Button onClick={crearNuevaAsignacion} variant="contained" startIcon={<AssignmentTurnedInIcon />} disabled={guardando || !nuevaAsignacionForm.plantilla_id}>
              {guardando ? 'Guardando...' : 'Asignar'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog: Confirmar eliminar asignacion */}
        <Dialog open={openConfirmarEliminarAsignacion} onClose={() => setOpenConfirmarEliminarAsignacion(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Confirmar Eliminacion de Asignacion</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 1 }}>
              {asignacionEliminar && (
                <Typography>
                  ¿Eliminar la asignacion de <strong>{tiposDocumento.find((t) => t.codigo === asignacionEliminar.tipo_comprobante)?.nombre} {asignacionEliminar.formato}</strong>{' '}
                  ({asignacionEliminar.sucursal_id ? (asignacionEliminar.sucursal_nombre || `Sucursal ${asignacionEliminar.sucursal_id}`) : 'Global'})?
                </Typography>
              )}
              <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                El PDF de ese tipo seguira funcionando pero usara la siguiente regla disponible (otra sucursal o plantilla por defecto).
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenConfirmarEliminarAsignacion(false)}>Cancelar</Button>
            <Button onClick={ejecutarEliminarAsignacion} variant="contained" color="error" startIcon={<DeleteIcon />} disabled={guardando}>
              {guardando ? 'Eliminando...' : 'Eliminar'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* ── Dialog: Gestion de Tipos de Documento ─────────────────────── */}
        <Dialog open={openGestionTipos} onClose={() => setOpenGestionTipos(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Tipos de Documento</span>
              <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={abrirNuevoTipo}>
                Nuevo tipo
              </Button>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Alert severity="info" sx={{ mb: 2 }}>
              Los tipos SUNAT (electronicos) no se pueden eliminar. Solo puedes agregar, editar o eliminar tipos internos.
            </Alert>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Codigo</TableCell>
                    <TableCell>Nombre</TableCell>
                    <TableCell>Descripcion</TableCell>
                    <TableCell align="center">Orden</TableCell>
                    <TableCell align="center">Tipo</TableCell>
                    <TableCell align="center">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tiposDocumento.map((t) => (
                    <TableRow key={t.id} hover>
                      <TableCell><strong>{t.codigo}</strong></TableCell>
                      <TableCell>{t.nombre}</TableCell>
                      <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>{t.descripcion || '-'}</TableCell>
                      <TableCell align="center">{t.orden}</TableCell>
                      <TableCell align="center">
                        {t.es_electronico
                          ? <Chip label="SUNAT" size="small" color="warning" />
                          : <Chip label="Interno" size="small" color="info" />}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Editar">
                          <Button size="small" onClick={() => abrirEditarTipo(t)}><EditIcon fontSize="small" /></Button>
                        </Tooltip>
                        {!t.es_electronico && (
                          <Tooltip title="Eliminar">
                            <Button size="small" color="error" onClick={() => confirmarEliminarTipo(t)}><DeleteIcon fontSize="small" /></Button>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenGestionTipos(false)}>Cerrar</Button>
          </DialogActions>
        </Dialog>

        {/* Dialog: Nuevo tipo de documento */}
        <Dialog open={openNuevoTipo} onClose={() => setOpenNuevoTipo(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Nuevo Tipo de Documento</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 1, display: 'grid', gap: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <TextField
                    label="Codigo"
                    fullWidth
                    value={tipoForm.codigo}
                    onChange={(e) => setTipoForm((p) => ({ ...p, codigo: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') }))}
                    inputProps={{ maxLength: 10 }}
                    helperText="Ej: COT, NV, OC"
                  />
                </Grid>
                <Grid item xs={8}>
                  <TextField
                    label="Nombre"
                    fullWidth
                    value={tipoForm.nombre}
                    onChange={(e) => setTipoForm((p) => ({ ...p, nombre: e.target.value }))}
                    helperText="Ej: Cotizacion, Nota de Venta"
                  />
                </Grid>
              </Grid>
              <TextField
                label="Descripcion (opcional)"
                fullWidth
                value={tipoForm.descripcion}
                onChange={(e) => setTipoForm((p) => ({ ...p, descripcion: e.target.value }))}
              />
              <TextField
                label="Orden de visualizacion"
                type="number"
                value={tipoForm.orden}
                onChange={(e) => setTipoForm((p) => ({ ...p, orden: Number(e.target.value) }))}
                sx={{ width: 180 }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenNuevoTipo(false)}>Cancelar</Button>
            <Button onClick={crearTipo} variant="contained" startIcon={<SaveIcon />} disabled={guardando || !tipoForm.codigo.trim() || !tipoForm.nombre.trim()}>
              {guardando ? 'Guardando...' : 'Crear tipo'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog: Editar tipo de documento */}
        <Dialog open={openEditarTipo} onClose={() => setOpenEditarTipo(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Editar Tipo de Documento</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 1, display: 'grid', gap: 2 }}>
              {tipoEditando && (
                <Box sx={{ p: 1.5, background: '#f5f5f5', borderRadius: 2 }}>
                  <Typography variant="body2">Codigo: <strong>{tipoEditando.codigo}</strong>{tipoEditando.es_electronico ? ' (tipo SUNAT, no editable el codigo)' : ''}</Typography>
                </Box>
              )}
              <TextField
                label="Nombre"
                fullWidth
                value={tipoForm.nombre}
                onChange={(e) => setTipoForm((p) => ({ ...p, nombre: e.target.value }))}
              />
              <TextField
                label="Descripcion (opcional)"
                fullWidth
                value={tipoForm.descripcion}
                onChange={(e) => setTipoForm((p) => ({ ...p, descripcion: e.target.value }))}
              />
              <TextField
                label="Orden"
                type="number"
                value={tipoForm.orden}
                onChange={(e) => setTipoForm((p) => ({ ...p, orden: Number(e.target.value) }))}
                sx={{ width: 180 }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenEditarTipo(false)}>Cancelar</Button>
            <Button onClick={guardarEditarTipo} variant="contained" startIcon={<SaveIcon />} disabled={guardando || !tipoForm.nombre.trim()}>
              {guardando ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog: Confirmar eliminar tipo */}
        <Dialog open={openConfirmarEliminarTipo} onClose={() => setOpenConfirmarEliminarTipo(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Confirmar Eliminacion</DialogTitle>
          <DialogContent>
            <Typography>
              ¿Eliminar el tipo <strong>{tipoEliminar?.nombre}</strong> ({tipoEliminar?.codigo})?
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
              Solo se puede eliminar si no tiene asignaciones activas de plantilla.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenConfirmarEliminarTipo(false)}>Cancelar</Button>
            <Button onClick={ejecutarEliminarTipo} variant="contained" color="error" startIcon={<DeleteIcon />} disabled={guardando}>
              {guardando ? 'Eliminando...' : 'Eliminar'}
            </Button>
          </DialogActions>
        </Dialog>

      </Box>
    </Box>
  );
}
